import { ConditionComparatorEnum, WhereClauseCondition } from "../ars_ldm";
import { normaliseDatasetVariable } from "./normaliseDatasetVariable";

// Comparator map: keys are possible tokens found in code (symbols or names).
export const comparatorDefinitions: {
  [key: string]: {
    enum: ConditionComparatorEnum;
    write: string;
    read: string[];
  };
} = {
  EQ: {
    enum: ConditionComparatorEnum.EQ,
    write: "=",
    read: ["EQ", "="],
  },
  NE: {
    enum: ConditionComparatorEnum.NE,
    write: "!=",
    read: ["NE", "!=", "<>"],
  },
  GT: {
    enum: ConditionComparatorEnum.GT,
    write: ">",
    read: ["GT", ">"],
  },
  GE: {
    enum: ConditionComparatorEnum.GE,
    write: ">=",
    read: ["GE", ">="],
  },
  LT: {
    enum: ConditionComparatorEnum.LT,
    write: "<",
    read: ["LT", "<"],
  },
  LE: {
    enum: ConditionComparatorEnum.LE,
    write: "<=",
    read: ["LE", "<="],
  },
  IN: {
    enum: ConditionComparatorEnum.IN,
    write: "IN",
    read: ["IN"],
  },
  NOTIN: {
    enum: ConditionComparatorEnum.NOTIN,
    write: "NOT IN",
    read: ["NOTIN", "NOT IN"],
  },
};

/**
 * Builds a regex pattern group from read strings, escaping special regex characters.
 * Sorts by length (descending) to ensure longer patterns like "NOT IN" match before "IN".
 *
 * @param readStrings - Array of strings that should match this comparator.
 * @returns Escaped pattern string for use in regex alternation.
 */
const buildComparatorPattern = (readStrings: string[]): string => {
  return readStrings
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
};

/**
 * Finds the comparator definition that matches the given token.
 *
 * @param token - The comparator token to look up.
 * @returns The matching comparator definition or undefined.
 */
const findComparatorDef = (
  token: string,
): { enum: ConditionComparatorEnum; write: string; read: string[] } | undefined => {
  const upperToken = token.toUpperCase();
  return Object.values(comparatorDefinitions).find((def) =>
    def.read.map((r) => r.toUpperCase()).includes(upperToken),
  );
};

/**
 * Parses a where clause code string into a WhereClauseCondition object.
 *
 * @param code - The code string to parse (e.g., "ADSL.SAFFL = 'Y'").
 * @param availableVariables - List of available dataset.variable combinations for validation.
 * @returns The parsed WhereClauseCondition metadata or false if parsing fails.
 */
export interface ParseConditionFromCodeSuccess {
  /** Parsed condition when the input is valid. */
  condition: WhereClauseCondition;
  /** Whether the parsed condition accepted an unlisted dataset.variable value. */
  acceptedUnlistedVariable: boolean;
}
export type ParseConditionFromCodeResult = false | ParseConditionFromCodeSuccess;

/**
 * Parses a where clause condition and always returns metadata describing whether an
 * unlisted dataset.variable was accepted.
 *
 * @param code - The code string to parse.
 * @param availableVariables - List of available dataset.variable combinations for validation.
 * @returns The parsed condition metadata or false if parsing fails.
 */
export function parseConditionFromCode(
  code: string,
  availableVariables: string[],
): ParseConditionFromCodeResult {
  if (!code || code.trim() === "") {
    return false;
  }

  const trimmed = code.trim();

  // Build regex patterns from comparator definitions
  const singleValueComparators: string[] = [];
  const listValueComparators: string[] = [];

  Object.values(comparatorDefinitions).forEach((def) => {
    const pattern = buildComparatorPattern(def.read);
    if (def.enum === ConditionComparatorEnum.IN || def.enum === ConditionComparatorEnum.NOTIN) {
      listValueComparators.push(pattern);
    } else {
      singleValueComparators.push(pattern);
    }
  });

  // Sort patterns by length (descending) to match longer patterns first
  const sortedSingleValueComparators = singleValueComparators.sort((a, b) => b.length - a.length);
  const sortedListValueComparators = listValueComparators.sort((a, b) => b.length - a.length);

  // Allow optional spaces around comparators (e.g., ADSL.SAFFL='Y' or ADSL.SAFFL = 'Y')
  const singleValuePattern = new RegExp(
    `^([A-Z0-9_]+)\\.([A-Z0-9_]+)\\s*(${sortedSingleValueComparators.join("|")})\\s*(?:'([^']*)'|"([^"]*)"|([^\\s]+))$`,
    "i",
  );
  const inPattern = new RegExp(
    `^([A-Z0-9_]+)\\.([A-Z0-9_]+)\\s*(${sortedListValueComparators.join("|")})\\s*\\(([^)]+)\\)$`,
    "i",
  );

  let match = trimmed.match(singleValuePattern);
  if (match) {
    const [, dataset, variable, comparator, singleQuotedValue, doubleQuotedValue, unquotedValue] =
      match;
    const datasetVariable = `${dataset.toUpperCase()}.${variable.toUpperCase()}`;
    const acceptedUnlistedVariable = normaliseDatasetVariable(datasetVariable) === datasetVariable;
    const isAvailableVariable = availableVariables
      .map((v) => v.toUpperCase())
      .includes(datasetVariable);

    // Validate that the variable exists
    if (!isAvailableVariable && !acceptedUnlistedVariable) {
      return false;
    }

    const value =
      singleQuotedValue !== undefined
        ? singleQuotedValue
        : doubleQuotedValue !== undefined
          ? doubleQuotedValue
          : unquotedValue;
    const def = findComparatorDef(comparator);

    return def
      ? {
          condition: {
            dataset: dataset.toUpperCase(),
            variable: variable.toUpperCase(),
            comparator: def.enum,
            value: [value],
          },
          acceptedUnlistedVariable: !isAvailableVariable && acceptedUnlistedVariable,
        }
      : // istanbul ignore next
        false;
  }

  match = trimmed.match(inPattern);
  if (match) {
    const [, dataset, variable, comparator, valuesString] = match;
    const datasetVariable = `${dataset.toUpperCase()}.${variable.toUpperCase()}`;
    const acceptedUnlistedVariable = normaliseDatasetVariable(datasetVariable) === datasetVariable;
    const isAvailableVariable = availableVariables
      .map((v) => v.toUpperCase())
      .includes(datasetVariable);

    // Validate that the variable exists
    if (!isAvailableVariable && !acceptedUnlistedVariable) {
      return false;
    }

    // Parse values from the IN clause
    const values = valuesString
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0)
      .map((v) => {
        // Remove quotes if present
        if ((v.startsWith("'") && v.endsWith("'")) || (v.startsWith('"') && v.endsWith('"'))) {
          return v.slice(1, -1);
        }
        return v;
      });

    if (values.length === 0) {
      return false;
    }

    const def = findComparatorDef(comparator);
    // istanbul ignore next - defensive check, should never fail since regex is built from definitions

    return def
      ? {
          condition: {
            dataset: dataset.toUpperCase(),
            variable: variable.toUpperCase(),
            comparator: def.enum,
            value: values,
          },
          acceptedUnlistedVariable: !isAvailableVariable && acceptedUnlistedVariable,
        }
      : // istanbul ignore next
        false;
  }
  // No match found
  return false;
}
